import { createAction } from '@reduxjs/toolkit'

import type { PriceTypeRef, ShiftCommandKind, StartMode } from './types'

/**
 * Команды РМК — plain actions (F05): экран → binding → команда → listener →
 * запрос. Ключ идемпотентности создаёт listener ОДИН раз на команду.
 * Действующее лицо в команде не передаётся: его знает только токен.
 *
 * Касса — устройство кабинета (`deviceId`, RP-C9). `registerKey` в командах над своей работой — ключ
 * кассы, который отдаёт служба (ключ ждущих команд смены и намерений продажи), а не рабочее место.
 */
interface Register {
  resourceId: string
  deviceId: number
}

/** Ключ ждущих команд смены: производный ключ кассы из ответа службы. */
interface Seat {
  resourceId: string
  registerKey: string
}

interface OwnSession extends Register {
  registerKey: string
  sessionId: string
  /** Версия сессии, которую видел человек. */
  expectedVersion: number
}

/**
 * «Открыть смену» / «Продолжить смену» одним действием (RP-FB22 3): проверка связи → работа за кассой с кассиром, видом цены
 * и подтверждением кода → смена. Подтверждение кода в действие не кладётся: слушатель берёт его из состояния сам, чтобы подпись
 * службы не ходила по экранам. `priceType`: `null` — «Цена кассы», не передан — как до RP-FB22.
 */
export const registerWorkRequested = createAction<
  Register & {
    mode: StartMode
    cashierId?: string
    cashierName?: string
    /** У кассира есть вход (`login.kind ≠ none`): без живого подтверждения команда не уходит. */
    cashierNeedsCode?: boolean
    /** Работа под своим именем (в источнике нет кассиров): служба требует сказать это явно. */
    asSelf?: boolean
    priceType?: PriceTypeRef | null
    /** Для экрана ожидания и памяти «как в прошлый раз». */
    registerName?: string
    priceTypeId?: string
  }
>('aqsi/rmk/registerWorkRequested')
export { cashierVerifyRequested } from './cashierVerification'
export { workplaceMoveRequested } from './moveWorkplace'
export const sessionCloseRequested = createAction<OwnSession>('aqsi/rmk/sessionCloseRequested')
/** RP-FB11: вид цены своей работы; источник и ключ `null` — «Цена кассы». */
export const sessionPriceTypeRequested = createAction<
  OwnSession & { priceTypeResourceId: string | null; priceTypeKey: string | null; name: string }
>('aqsi/rmk/sessionPriceTypeRequested')
export const sessionHeartbeatRequested = createAction<OwnSession>('aqsi/rmk/sessionHeartbeatRequested')
export const sessionTakeoverRequested = createAction<Register & { sessionId: string; expectedVersion: number; reason: string }>(
  'aqsi/rmk/sessionTakeoverRequested',
)
export const sessionCloseForeignRequested = createAction<
  Register & { sessionId: string; expectedVersion: number; reason: string }
>('aqsi/rmk/sessionCloseForeignRequested')
export const shiftAdoptRequested = createAction<OwnSession>('aqsi/rmk/shiftAdoptRequested')
export const shiftCommandRequested = createAction<OwnSession & { kind: ShiftCommandKind }>(
  'aqsi/rmk/shiftCommandRequested',
)
/** «Проверить статус»: ЧТЕНИЕ операции, а не повтор команды. */
export const operationCheckRequested = createAction<Seat & { operationId: string }>('aqsi/rmk/operationCheckRequested')
/** «Сверить с кабинетом»: служба только читает кабинет (операцию и факт смены). */
export const operationReconcileRequested = createAction<Seat & { operationId: string; expectedVersion: number }>(
  'aqsi/rmk/operationReconcileRequested',
)
/**
 * Ручная сверка команды смены с неизвестным исходом (UI-ACTIONS P1 №5): служба
 * её принимает, кабинет не вызывается. Один POST на нажатие; повтора нет.
 */
export const shiftOperationResolveRequested = createAction<
  Seat & { operationId: string; expectedVersion: number; outcome: 'Succeeded' | 'Failed'; reason: string; checked: string }
>('aqsi/rmk/shiftOperationResolveRequested')
/** «Проверить кассу»: служебная проверка двух уровней связи, один POST без повтора. */
export const linkCheckRequested = createAction<Register>('aqsi/rmk/linkCheckRequested')
export const rmkReloadRequested = createAction<{ resourceId: string; deviceId: number | null }>('aqsi/rmk/reloadRequested')
